import { Button, Checkbox, Flex, Form, Input, Radio, Select, Typography } from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import Joi from "joi";
import { Loader } from "components/Loader";
import { PhoneAntd } from "components/PhoneAntd";
import { SummaryTitle } from "components/StartContent/styled";
import { LEGAL_FORM, TRACKING } from "config/consts";
import { useApp } from "contexts/AppProvider";
import { useCreateReservationMutation, useGetFinanceDetailsQuery } from "queries/reservation.query";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import { useNavigate } from "react-router-dom";
import { createReservationParam } from "repositories/reservations/reservationRepository.param";
import { uniqueOptionIds } from "lib/optionIds";
import { redirectToPayment } from "lib/paymentRedirect";
import {
  canOfferPaymentLinkBeforePatientLookup,
  getEffectivePublicReservationExpiryMinutes,
  PaymentLinkMode,
} from "lib/paymentPolicy";

const emailSchema = Joi.string().email();

export const PatientForm = () => {
  const {
    customerId,
    setActiveStep,
    reservationPayload,
    activeTimeslot,
    branches,
    setConfirmationData,
    mainColor,
    referrals,
    settings,
    price,
    currency,
    patientFormValues,
    setPatientFormValues,
  } = useApp();
  const [form] = Form.useForm();
  const createReservationMutation = useCreateReservationMutation();
  const navigate = useNavigate();
  const { formatMessage, locale } = useIntl();
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);
  const isOnlineVisit = Boolean(reservationPayload.isOnline || activeTimeslot?.isOnline);
  const paymentLinkMode = settings?.preferences?.paymentLinkMode ?? PaymentLinkMode.ALWAYS;
  const canOfferPaymentLink = canOfferPaymentLinkBeforePatientLookup({
    mode: paymentLinkMode,
    online: isOnlineVisit,
  });
  const shouldShowPaymentMethods =
    Number(price) > 0 && Boolean(activeTimeslot?.therapist.hasOnlinePayments) && canOfferPaymentLink;
  const reservationExpiryMinutes = getEffectivePublicReservationExpiryMinutes({
    mode: paymentLinkMode,
    online: isOnlineVisit,
    publicReservationExpiryMinutes: settings?.preferences?.publicReservationExpiryMinutes ?? 0,
    onlineReservationExpiryMinutes: settings?.preferences?.onlineReservationExpiryMinutes ?? 0,
  });
  // Any non-zero value means the reservation expires and the patient needs a
  // payment link — negative values set the deadline before the visit start.
  const hasReservationExpiry = reservationExpiryMinutes !== 0;
  const maxBankTransferHours = settings?.preferences?.maxBankTransferHours;
  const shouldShowFirstVisitPrepaymentNotice =
    Number(price) > 0 && Number.isFinite(maxBankTransferHours) && maxBankTransferHours !== 0 && !hasReservationExpiry;
  const requiresOnlinePayment = shouldShowPaymentMethods && hasReservationExpiry;
  const hasBankAccountNumber = Boolean(settings?.customer.bankAccountNumber?.trim());
  // Mirrors the server clamp: a deadline that is not in the future is moved to
  // the visit start, so the patient has at most the time until the visit.
  const minutesUntilVisitStart = activeTimeslot
    ? dayjs(
        `${activeTimeslot.date} ${activeTimeslot.startTime.length === 5 ? `${activeTimeslot.startTime}:00` : activeTimeslot.startTime}`,
        "YYYY-MM-DD HH:mm:ss",
      ).diff(dayjs(), "minute")
    : 0;
  const clampedExpiryMinutes = Math.max(minutesUntilVisitStart, 0);
  // A negative value is a deadline before the visit start. The server keeps it
  // whenever it is still in the future and only clamps to the visit start once
  // the deadline is already behind us — mirror that check for the copy.
  const displaysClampedDeadline =
    reservationExpiryMinutes < 0 && minutesUntilVisitStart + reservationExpiryMinutes <= 0;

  useEffect(() => {
    if (patientFormValues) {
      form.setFieldsValue(patientFormValues);
    }
  }, []);

  useEffect(() => {
    if (requiresOnlinePayment) {
      form.setFieldValue("paymentMethod", "online");
    }
  }, [form, requiresOnlinePayment]);

  const handleChange = (phoneNumber) => {
    form.setFieldsValue({ phoneNumber });
  };

  const { data: whoisData } = useGetFinanceDetailsQuery(
    customerId,
    activeTimeslot.therapist.user.id,
    activeTimeslot.branchId,
  );

  useEffect(() => {
    setActiveStep(TRACKING.FORM);
  }, []);

  const onFinish = (values) => {
    setIsLoaderVisible(true);
    const parsed = values.phoneNumber ? parsePhoneNumber(values.phoneNumber) : null;

    if (!parsed || !parsed.isValid()) {
      form.setFields([
        {
          name: "phoneNumber",
          errors: [formatMessage({ defaultMessage: "Wprowadź poprawny numer telefonu", id: "+V8Wzt" })],
        },
      ]);
      return;
    }

    const payload: createReservationParam = {
      patient: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        notes: values.notes || null,
        phoneNumber: `${parsed.countryCallingCode}-${parsed.nationalNumber}`,
        birthYear: reservationPayload.birthYear || null,
        referralId: values.referralId,
      },
      startTime: `${activeTimeslot.startTime}:00`,
      scheduleId: activeTimeslot.scheduleId,
      optionIds: uniqueOptionIds(reservationPayload.optionIds),
      serviceId: reservationPayload.serviceId,
    };

    createReservationMutation.mutate(
      { data: payload },
      {
        onSuccess: ({ data }) => {
          setPatientFormValues(null);
          setActiveStep(TRACKING.FINALIZED);
          setConfirmationData({
            date: activeTimeslot.date,
            startTime: activeTimeslot.startTime,
            therapist: `${activeTimeslot.therapist.user.firstName} ${activeTimeslot.therapist.user.lastName}`,
            isOnline: isOnlineVisit,
            branch: isOnlineVisit ? null : branches.find((el) => el.id === activeTimeslot.branchId),
            price: data.result.price,
            bankAccountNumber: data.result.bankAccountNumber,
            firstTime: data.result.firstTime,
            parentAloneOnFirstVisit: data.result.parentAloneOnFirstVisit,
          });
          if (shouldShowPaymentMethods && values.paymentMethod === "online" && data.result.paymentLink) {
            let paymentCode = "";

            try {
              paymentCode = new URL(data.result.paymentLink).searchParams.get("id") || "";
            } catch {
              paymentCode = "";
            }

            redirectToPayment({ paymentLink: data.result.paymentLink, paymentCode });
          } else {
            navigate(`/thank-you${location.search}`);
          }
        },
        onError: (err: AxiosError) => {
          console.error(err);
        },
      },
    );
  };

  if (isLoaderVisible) {
    return <Loader />;
  }
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={(_, all) => setPatientFormValues(all)}
      initialValues={{
        paymentMethod: requiresOnlinePayment ? "online" : undefined,
      }}
    >
      <SummaryTitle>{formatMessage({ defaultMessage: "Podsumowanie wizyty", id: "XXk/2e" })}</SummaryTitle>
      <div style={{ marginBottom: 20 }}>
        <div>
          <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
            {formatMessage({ defaultMessage: "Data", id: "XHHR08" })}:
          </Typography.Text>
          <Typography.Text strong>
            {dayjs(`${activeTimeslot.date} ${activeTimeslot.startTime}`, "YYYY-MM-DD HH:mm:ss").format(
              "DD MMMM YYYY, HH:mm",
            )}
          </Typography.Text>
        </div>
        <div>
          <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
            {formatMessage({ defaultMessage: "Specjalista", id: "dz8aKC" })}:
          </Typography.Text>
          <Typography.Text strong>
            {activeTimeslot.therapist.user.firstName} {activeTimeslot.therapist.user.lastName}
          </Typography.Text>
        </div>
        <div>
          <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
            {formatMessage({ defaultMessage: "Koszt wizyty", id: "qfbfoZ" })}:
          </Typography.Text>
          <Typography.Text strong>
            {`${price} 
            ${currency}`}
          </Typography.Text>
        </div>
      </div>
      <SummaryTitle>{formatMessage({ defaultMessage: "Dane pacjenta", id: "As91bu" })}</SummaryTitle>
      <div className="patient-form__contact-grid">
        <Form.Item
          label={formatMessage({ defaultMessage: "Imię", id: "qbTlMP" })}
          name="firstName"
          rules={[
            {
              required: true,
              message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }),
            },
            {
              min: 3,
              message: formatMessage({
                defaultMessage: "Imię musi mieć co najmniej 3 znaki",
                id: "3VTx7g",
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={formatMessage({ defaultMessage: "Nazwisko", id: "SFRENv" })}
          name="lastName"
          rules={[
            {
              required: true,
              message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }),
            },
            {
              min: 3,
              message: formatMessage({
                defaultMessage: "Nazwisko musi mieć co najmniej 3 znaki",
                id: "0oSLJD",
              }),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label={formatMessage({ defaultMessage: "Numer telefonu", id: "LGa4LH" })}
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }),
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                const parsed = parsePhoneNumber(value);
                if (!parsed?.isValid()) {
                  return Promise.reject(
                    new Error(formatMessage({ defaultMessage: "Wprowadź poprawny numer telefonu", id: "+V8Wzt" })),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <PhoneInput
            onChange={handleChange}
            defaultCountry="PL"
            placeholder={formatMessage({ defaultMessage: "Podaj numer telefonu", id: "Un1hH2" })}
            inputComponent={PhoneAntd}
          />
        </Form.Item>
        <Form.Item
          label={formatMessage({ defaultMessage: "Email", id: "sy+pv5" })}
          name="email"
          rules={[
            {
              required: true,
              message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }),
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                const { error } = emailSchema.validate(value);
                if (error) {
                  return Promise.reject(
                    new Error(formatMessage({ defaultMessage: "Wprowadź poprawny adres email", id: "WD2HK6" })),
                  );
                }

                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                const polishChars = /[ąćęłńóśżźĄĆĘŁŃÓŚŻŹ]/;
                if (polishChars.test(value)) {
                  return Promise.reject(
                    new Error(
                      formatMessage({
                        defaultMessage: "Adres email nie może zawierać polskich znaków",
                        id: "ObftHV",
                      }),
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input />
        </Form.Item>
      </div>
      <Form.Item
        label={formatMessage({ defaultMessage: "Wiadomość dla terapeuty", id: "nIawbd" })}
        name="notes"
        rules={[
          {
            max: 1000,
            message: formatMessage({
              defaultMessage: "Notatki nie mogą przekraczać 1000 znaków",
              id: "MKAuMu",
            }),
          },
        ]}
      >
        <Input.TextArea />
      </Form.Item>
      {referrals && referrals.length > 0 && (
        <Form.Item
          label={formatMessage({ defaultMessage: "Skąd dowiedziałeś się o nas?", id: "bgE20q" })}
          name="referralId"
          rules={[
            { required: true, message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }) },
          ]}
        >
          <Select
            allowClear
            placeholder={formatMessage({ defaultMessage: "Wybierz", id: "jQd4cK" })}
            options={referrals
              .filter((el) => el.language === "pl")
              .map((referral) => ({ label: referral.translations[locale], value: referral.id }))}
          />
        </Form.Item>
      )}

      {shouldShowPaymentMethods && (
        <>
          <h3 style={{ fontSize: "16px" }}>{formatMessage({ defaultMessage: "Metoda płatności", id: "iuoJ5z" })}</h3>

          <Form.Item
            style={{ marginBottom: 0 }}
            label=""
            name="paymentMethod"
            rules={[
              { required: true, message: formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" }) },
            ]}
          >
            <Radio.Group
              style={{
                display: "flex",
                flexDirection: "column",
              }}
              defaultValue={requiresOnlinePayment ? "online" : undefined}
            >
              <Radio value="online">
                <div>
                  <Flex align="center" gap={8}>
                    <span>Szybka płatność online</span>
                  </Flex>
                </div>
              </Radio>
              {!requiresOnlinePayment && hasBankAccountNumber && <Radio value="transfer">Przelew tradycyjny</Radio>}
            </Radio.Group>
          </Form.Item>
        </>
      )}
      {whoisData && (
        <div style={{ margin: "10px 0 15px" }}>
          {shouldShowPaymentMethods && (
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, lineHeight: 1.4 }}>
              {formatMessage({
                defaultMessage: "W przypadku płatności online operator płatności może doliczyć opłatę.",
                id: "UkZ9Yl",
              })}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {`Odbiorcą płatności za usługę jest ${whoisData.address.name}${whoisData.legalForm ? ` (${LEGAL_FORM[whoisData.legalForm]})` : ""}, z siedzibą przy ul.${whoisData.address.street} ${whoisData.address.houseNumber}${whoisData.address.roomNumber ? `/${whoisData.address.roomNumber}` : ""}, ${whoisData.address.postalCode} ${whoisData.address.city}${whoisData.taxId ? `, NIP: ${whoisData.taxId}` : ""}`}
          </Typography.Text>
        </div>
      )}
      {hasReservationExpiry && Number(price) > 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: "#FFF7E6",
            border: "1px solid #FFD591",
            borderRadius: 4,
          }}
        >
          <Typography.Text>
            {displaysClampedDeadline
              ? clampedExpiryMinutes < 60
                ? formatMessage(
                    {
                      defaultMessage: "Prosimy o opłacenie wizyty w ciągu {minutes} minut, aby potwierdzić rezerwację.",
                      id: "ZcU1Jx",
                    },
                    { minutes: clampedExpiryMinutes },
                  )
                : formatMessage(
                    {
                      defaultMessage:
                        "Prosimy o opłacenie wizyty w ciągu {hours, plural, one {# godziny} other {# godzin}}, aby potwierdzić rezerwację.",
                      id: "/ataQM",
                    },
                    { hours: clampedExpiryMinutes / 60 },
                  )
              : reservationExpiryMinutes < 0
                ? Math.abs(reservationExpiryMinutes) < 60
                  ? formatMessage(
                      {
                        defaultMessage:
                          "Prosimy o opłacenie wizyty najpóźniej {minutes} minut przed rozpoczęciem wizyty, aby potwierdzić rezerwację.",
                        id: "cOjPc3",
                      },
                      { minutes: Math.abs(reservationExpiryMinutes) },
                    )
                  : formatMessage(
                      {
                        defaultMessage:
                          "Prosimy o opłacenie wizyty najpóźniej {hours, plural, one {# godzinę} few {# godziny} other {# godzin}} przed rozpoczęciem wizyty, aby potwierdzić rezerwację.",
                        id: "0UbuTS",
                      },
                      { hours: Math.abs(reservationExpiryMinutes) / 60 },
                    )
                : reservationExpiryMinutes < 60
                  ? formatMessage(
                      {
                        defaultMessage:
                          "Prosimy o opłacenie wizyty w ciągu {minutes} minut, aby potwierdzić rezerwację.",
                        id: "ZcU1Jx",
                      },
                      { minutes: reservationExpiryMinutes },
                    )
                  : formatMessage(
                      {
                        defaultMessage:
                          "Prosimy o opłacenie wizyty w ciągu {hours, plural, one {# godziny} other {# godzin}}, aby potwierdzić rezerwację.",
                        id: "/ataQM",
                      },
                      { hours: reservationExpiryMinutes / 60 },
                    )}
          </Typography.Text>
        </div>
      )}
      {shouldShowFirstVisitPrepaymentNotice && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: "#FFF7E6",
            border: "1px solid #FFD591",
            borderRadius: 4,
          }}
        >
          <Typography.Text>
            {maxBankTransferHours > 0
              ? formatMessage(
                  {
                    defaultMessage:
                      "Jeśli to Twoja pierwsza wizyta, wymagana jest przedpłata w ciągu {hours, plural, one {# godziny} other {# godzin}} od umówienia wizyty. Brak wpłaty spowoduje anulowanie wizyty. Szczegóły płatności otrzymasz w wiadomości potwierdzającej wizytę.",
                    id: "Mw49Xi",
                  },
                  { hours: maxBankTransferHours },
                )
              : formatMessage(
                  {
                    defaultMessage:
                      "Jeśli to Twoja pierwsza wizyta, wymagana jest przedpłata najpóźniej {hours, plural, one {# godzinę} few {# godziny} other {# godzin}} przed rozpoczęciem wizyty. Brak wpłaty spowoduje anulowanie wizyty. Szczegóły płatności otrzymasz w wiadomości potwierdzającej wizytę.",
                    id: "N/E7Q0",
                  },
                  { hours: Math.abs(maxBankTransferHours) },
                )}
          </Typography.Text>
        </div>
      )}
      <h3 style={{ fontSize: "16px" }}>
        {formatMessage({ defaultMessage: "Zgoda na przetwarzanie danych", id: "8E3SMW" })}
      </h3>
      <Form.Item
        label=""
        name="agreement"
        valuePropName="checked"
        rules={[
          {
            required: true,
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(
                    new Error(formatMessage({ defaultMessage: "Konieczne jest wyrażenie zgody", id: "/eif8A" })),
                  ),
          },
        ]}
      >
        <Checkbox style={{ marginTop: "-5px" }}>
          <Typography.Text>
            {formatMessage({
              defaultMessage: "Przeczytałem i akceptuję zasady zawarte w",
              id: "OOLDUC",
            })}{" "}
            <a
              href={settings?.customer.privacyPolicyLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: mainColor, textDecoration: "underline" }}
            >
              {formatMessage({ defaultMessage: "Polityce Prywatności", id: "F8fTkv" })}
            </a>{" "}
            {formatMessage({
              defaultMessage: "oraz",
              id: "8bp5ze",
            })}{" "}
            <a
              href={settings?.customer.termsLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: mainColor, textDecoration: "underline" }}
            >
              {formatMessage({ defaultMessage: "Regulaminie.", id: "qPaP9z" })}
            </a>
          </Typography.Text>
        </Checkbox>
      </Form.Item>
      <Flex justify="space-between" style={{ marginTop: whoisData ? 20 : 0 }}>
        <Button type="default" onClick={() => navigate(-1)}>
          {formatMessage({ defaultMessage: "Wróć", id: "6mVXvn" })}
        </Button>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            onClick={() => {
              form.validateFields().catch((error) => {
                console.error("Validation failed:", error);
              });
            }}
          >
            {formatMessage({ defaultMessage: "Umów wizytę", id: "LOAXb+" })}
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};
